import { useState } from "react";
import { toast } from "sonner";
import { Check, X, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { diff } from "./ImportChanges";

const pretty = (f) => f.replace(/\[(\d+)\]/g, (_, i) => ` ${+i + 1}`).replace(/[._]/g, " ");

const Diff = ({ c, i }) => (
  <div data-testid={`fix-change-${i}`} className="py-2 border-t border-stone-100 text-xs leading-relaxed">
    <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-0.5">{pretty(c.field)}</div>
    {diff(c.before, c.after).map(([t, w], n) => t === "=" ? <span key={n}>{w}</span>
      : t === "-" ? <del key={n} className="bg-red-50 text-red-600">{w}</del> : <ins key={n} className="bg-emerald-50 text-emerald-700 no-underline">{w}</ins>)}
  </div>
);

const Verify = ({ v }) => {
  const ok = v.web_status === "verified";
  return (
    <div data-testid="fix-verify" className={`mt-2 rounded-sm px-2.5 py-2 text-[11px] ${ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>
      <div className="flex items-center gap-1.5 font-semibold">{ok ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />}
        {ok ? "Venue re-verified on the live web" : v.web_status === "closed" ? "Web search says this venue has closed" : "Web search couldn't confirm this venue"}</div>
      {v.evidence && <div className="mt-0.5 opacity-80">{v.evidence}</div>}
      {v.overrides?.map((o) => <div key={o.field} data-testid={`fix-override-${o.field}`} className="mt-1">Official {o.field} differs from your instruction — using <b>{o.official}</b> (you said "{o.yours}")</div>)}
      {!!v.unverified?.length && <div className="mt-1">Not confirmed on an official source: {v.unverified.join(", ")}</div>}
      {!ok && <div className="mt-1 font-semibold">Saving this will block publishing until the venue is fixed or replaced.</div>}
    </div>
  );
};

export const FixCard = ({ f, kitId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const act = async (what) => {
    setBusy(true);
    try {
      const { data } = await http.post(`/kits/${kitId}/page-fixes/${f.id}/${what}`);
      if (what === "apply") (data.blocked ? toast.warning : toast.success)(data.blocked ? "Saved — but this venue now blocks publishing" : "Fix saved — the PDF is re-rendering");
      onDone();
    } catch (e) { toast.error(errMsg(e)); onDone(); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`page-fix-${f.id}`} className="rounded-sm border border-stone-200 bg-white p-3">
      <div className="text-[11px] font-semibold text-[#2B2A26]">{f.label} <span className="font-normal text-stone-400">· page {f.page + 1}</span></div>
      <div className="text-xs text-stone-500 italic mt-0.5">"{f.instruction}"</div>
      {f.status === "working" && <div data-testid="page-fix-working" className="mt-2 flex items-center gap-2 text-xs text-[#9A7B2C]"><Loader2 size={13} className="animate-spin" /> {f.stage || "Working"}…</div>}
      {f.status === "failed" && <div data-testid="page-fix-error" className="mt-2 text-xs text-red-600">{f.error}</div>}
      {f.status === "ready" && <>
        {f.summary && <div data-testid="page-fix-summary" className="mt-2 text-xs text-stone-700">{f.summary}</div>}
        {f.verify && <Verify v={f.verify} />}
        <div className="mt-2 max-h-72 overflow-y-auto">{f.changes.map((c, i) => <Diff key={c.field} c={c} i={i} />)}</div>
      </>}
      {f.status !== "working" && <div className="mt-2 flex gap-2">
        {f.status === "ready" && <button data-testid="page-fix-approve" disabled={busy} onClick={() => act("apply")} className="inline-flex items-center gap-1 rounded-sm bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700 disabled:opacity-40"><Check size={12} /> Approve & save</button>}
        <button data-testid="page-fix-discard" disabled={busy} onClick={() => act("discard")} className="inline-flex items-center gap-1 rounded-sm border border-stone-300 px-3 py-1.5 text-[11px] font-bold text-stone-600 hover:bg-stone-100 disabled:opacity-40"><X size={12} /> {f.status === "ready" ? "Discard" : "Dismiss"}</button>
      </div>}
    </div>
  );
};
